'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface PaymentEntryFormProps {
  onClose: () => void;
}

interface PaymentFormData {
  direction: string;
  lotNumber: string;
  party: string;
  method: string;
  amount: number;
  utr: string;
  date: string;
  remarks: string;
}

export default function PaymentEntryForm({ onClose }: PaymentEntryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<PaymentFormData>({
    defaultValues: { direction: 'received' },
  });

  const method = watch('method');

  const onSubmit = async (data: PaymentFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/payments with tenant_id, lot_id, duplicate UTR check
    await new Promise((r) => setTimeout(r, 1000));
    setIsSubmitting(false);
    toast.success('Payment recorded successfully');
    onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
      {/* Direction toggle */}
      <div>
        <label className="block text-xs font-semibold text-foreground mb-2">Payment Direction</label>
        <div className="grid grid-cols-2 gap-2">
          {['received', 'sent'].map((dir) => (
            <label
              key={`dir-${dir}`}
              className="flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-all has-[:checked]:border-primary has-[:checked]:bg-primary/5"
            >
              <input
                type="radio"
                value={dir}
                {...register('direction')}
                className="sr-only"
              />
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                dir === 'received' ? 'border-accent' : 'border-info'
              }`}>
                <div className={`w-2 h-2 rounded-full ${dir === 'received' ? 'bg-accent' : 'bg-info'}`} />
              </div>
              <span className="text-sm font-medium capitalize">{dir === 'received' ? '↓ Received' : '↑ Sent'}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Lot Number <span className="text-danger">*</span>
          </label>
          <select {...register('lotNumber', { required: 'Required' })} className="input-field">
            <option value="">Select lot</option>
            {['300', '470', '410', '185', '211', '330'].map((l) => (
              <option key={`pay-lot-${l}`} value={l}>Lot #{l}</option>
            ))}
          </select>
          {errors.lotNumber && <p className="text-2xs text-danger mt-0.5">{errors.lotNumber.message}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Party Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            {...register('party', { required: 'Required' })}
            placeholder="Buyer / Distributor name"
            className="input-field"
          />
          {errors.party && <p className="text-2xs text-danger mt-0.5">{errors.party.message}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Payment Method <span className="text-danger">*</span>
          </label>
          <select {...register('method', { required: 'Required' })} className="input-field">
            <option value="">Select method</option>
            <option value="UPI">UPI</option>
            <option value="Bank Transfer">Bank Transfer (NEFT/RTGS)</option>
            <option value="Cash">Cash</option>
            <option value="Cheque">Cheque</option>
          </select>
          {errors.method && <p className="text-2xs text-danger mt-0.5">{errors.method.message}</p>}
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1.5">
            Amount (₹) <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            {...register('amount', { required: 'Required', min: { value: 1, message: 'Min ₹1' } })}
            placeholder="0.00"
            className="input-field font-mono"
          />
          {errors.amount && <p className="text-2xs text-danger mt-0.5">{errors.amount.message}</p>}
        </div>
      </div>

      {(method === 'UPI' || method === 'Bank Transfer') && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              UTR / Reference Number
              <p className="text-2xs text-muted-foreground font-normal mt-0.5">Auto-detected for duplicates</p>
            </label>
            <input
              type="text"
              {...register('utr')}
              placeholder="UTR123456789"
              className="input-field font-mono"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">Payment Date</label>
            <input
              type="date"
              {...register('date')}
              className="input-field"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-semibold text-foreground mb-1.5">Remarks</label>
        <textarea
          {...register('remarks')}
          rows={2}
          placeholder="Payment notes..."
          className="input-field resize-none"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        <button type="submit" disabled={isSubmitting} className="btn-primary flex-1">
          {isSubmitting ? (
            <><Loader2 size={14} className="animate-spin" />Saving...</>
          ) : (
            'Save Payment'
          )}
        </button>
      </div>
    </form>
  );
}